// Customize mode — one editing session over an object's tenant overlay.
//
// Every change is a small set/reset diff sent through the host transport and
// saved at once (the server's overlay_policy decides what is allowed). The
// session keeps the object's EFFECTIVE model (core + this organization's
// overlay) so the screen re-renders from it, undo / redo stacks of diffs,
// and a saving / saved / error status with the server's refusal reasons.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  policyErrors,
  type CustomizeObjType, type CustomizeTransport, type ModelOverlayResult, type OverlaySetLeafLike,
  type PatchExtras, type PatchResult,
} from './model'

/** One change. Customize sends set / reset leaves of the overlay; Make
 *  changes (the build transport) may also carry array `ops` — the extras
 *  ride along to the transport and an overlay transport ignores them. */
export interface CustomizeDiff extends PatchExtras {
  set: OverlaySetLeafLike[]
  reset: string[]
}

export type CustomizeStatus = 'loading' | 'idle' | 'saving' | 'saved' | 'error'

export interface CustomizeSession {
  objType: CustomizeObjType
  objName: string
  status: CustomizeStatus
  /** The server's reasons for the last refused change (empty when none). */
  errors: string[]
  overlay: Record<string, unknown>
  effective: Record<string, unknown> | null
  core: Record<string, unknown> | null
  /** Save a change. Resolves true when stored, false when refused. */
  apply: (diff: CustomizeDiff, label?: string) => Promise<boolean>
  undo: () => Promise<void>
  canUndo: boolean
  /** The label of the change Undo would revert. */
  undoLabel: string
  /** Re-apply the change Undo last reverted. */
  redo: () => Promise<void>
  canRedo: boolean
  /** The label of the change Redo would re-apply. */
  redoLabel: string
  /** Drop every customization under these overlay path prefixes. */
  resetUnder: (prefixes: string[], label?: string) => Promise<boolean>
  /** Drop every customization of the object. */
  resetAll: () => Promise<boolean>
  /** Whether the overlay holds a value at this exact path. */
  isCustomized: (path: string) => boolean
  /** Number of customized leaves (optionally only under a prefix). */
  countCustomized: (prefix?: string) => number
  dismissErrors: () => void
  /** The labels of the changes stored in this session, oldest first (an
   *  undone one leaves, a redone one comes back) — what "Save a version"
   *  writes its description from. */
  changeLog: string[]
  /** Notes the server returned with the last stored change (saved, but
   *  something that follows it did not happen). */
  warnings: string[]
}

// ── overlay document helpers ─────────────────────────────────────────────

const KEY_FIELD = '@key'

export function overlayValueAt(doc: unknown, path: string): { present: boolean; value?: unknown } {
  let node: unknown = doc
  const segs = path.split('.')
  for (let i = 0; i < segs.length; i++) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return { present: false }
    const rec = node as Record<string, unknown>
    if (!Object.prototype.hasOwnProperty.call(rec, segs[i])) return { present: false }
    node = rec[segs[i]]
  }
  return { present: true, value: node }
}

/** Every leaf path of an overlay document (a non-object value, or an empty
 *  object), dotted, `@key` annotations skipped. */
export function overlayLeafPaths(doc: unknown, prefix = ''): string[] {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return prefix ? [prefix] : []
  const out: string[] = []
  const entries = Object.entries(doc as Record<string, unknown>)
  if (!entries.length) return prefix ? [prefix] : []
  for (const [k, v] of entries) {
    if (k === KEY_FIELD) continue
    const p = prefix ? `${prefix}.${k}` : k
    if (v && typeof v === 'object' && !Array.isArray(v)) out.push(...overlayLeafPaths(v, p))
    else out.push(p)
  }
  return out
}

/** The diff that reverts `diff` when applied to `overlay`. */
export function inverseDiff(overlay: unknown, diff: CustomizeDiff): CustomizeDiff {
  const inv: CustomizeDiff = { set: [], reset: [] }
  const seen = new Set<string>()
  for (const leaf of diff.set) {
    if (seen.has(leaf.path)) continue
    seen.add(leaf.path)
    const prev = overlayValueAt(overlay, leaf.path)
    if (prev.present) inv.set.push({ path: leaf.path, value: prev.value })
    else inv.reset.push(leaf.path)
  }
  for (const path of diff.reset) {
    if (seen.has(path)) continue
    seen.add(path)
    const prev = overlayValueAt(overlay, path)
    if (prev.present) inv.set.push({ path, value: prev.value })
  }
  return inv
}

// ── the hook ─────────────────────────────────────────────────────────────

export function useCustomizeSession(opts: {
  objType: CustomizeObjType
  objName: string
  transport: CustomizeTransport
  /** False = dormant (no load). */
  active: boolean
  /** Called with every new effective model (after load and after each save). */
  onEffective?: (res: ModelOverlayResult) => void
}): CustomizeSession {
  const { objType, objName, transport, active } = opts
  const onEffectiveRef = useRef(opts.onEffective)
  onEffectiveRef.current = opts.onEffective
  const [status, setStatus] = useState<CustomizeStatus>('loading')
  const [errors, setErrors] = useState<string[]>([])
  const [res, setRes] = useState<ModelOverlayResult | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const logRef = useRef<string[]>([])
  // Each entry is the diff that moves the overlay one step back (undo) or
  // forward again (redo). A new change empties the redo stack.
  const undoStack = useRef<{ diff: CustomizeDiff; label: string }[]>([])
  const redoStack = useRef<{ diff: CustomizeDiff; label: string }[]>([])
  const [, bump] = useState(0)
  const overlayRef = useRef<Record<string, unknown>>({})

  const load = useCallback(async (): Promise<ModelOverlayResult | null> => {
    try {
      const r = await transport.get(objType, objName)
      overlayRef.current = (r.overlay || {}) as Record<string, unknown>
      setRes(r)
      onEffectiveRef.current?.(r)
      return r
    } catch (e) {
      setErrors(policyErrors(e))
      setStatus('error')
      return null
    }
  }, [transport, objType, objName])

  useEffect(() => {
    if (!active) return
    undoStack.current = []
    redoStack.current = []
    logRef.current = []
    setErrors([])
    setWarnings([])
    setStatus('loading')
    void load().then((r) => { if (r) setStatus('idle') })
  }, [active, load])

  // Store one change. Resolves the diff that undoes it (the transport's own
  // inverse when it returns one — a core model's — else the overlay
  // inverse computed beforehand), or null when refused.
  const send = useCallback(async (diff: CustomizeDiff): Promise<CustomizeDiff | null> => {
    const local = inverseDiff(overlayRef.current, diff)
    if (!diff.set.length && !diff.reset.length && !diff.ops?.length) return local
    setStatus('saving')
    setErrors([])
    try {
      const extras: PatchExtras = {}
      if (diff.ops?.length) extras.ops = diff.ops
      if (diff.leaves_first) extras.leaves_first = true
      if (diff.prune && Object.keys(diff.prune).length) extras.prune = diff.prune
      const out = (await transport.patch(
        objType, objName, diff.set, diff.reset, Object.keys(extras).length ? extras : undefined,
      )) as PatchResult | undefined
      if (out && out.effective && typeof out.effective === 'object') {
        // The transport answered with the new model: no second round trip.
        const r: ModelOverlayResult = {
          obj_type: objType, obj_name: objName, version: 0,
          core: out.core ?? out.effective, overlay: out.overlay ?? {}, effective: out.effective,
        }
        overlayRef.current = (r.overlay || {}) as Record<string, unknown>
        setRes(r)
        onEffectiveRef.current?.(r)
      } else {
        await load()
      }
      setWarnings(Array.isArray(out?.warnings) ? out!.warnings!.map(String) : [])
      setStatus('saved')
      if (out?.inverse) {
        return {
          set: out.inverse.set || [], reset: out.inverse.reset || [],
          ...(out.inverse.ops?.length ? { ops: out.inverse.ops } : {}),
          ...(out.inverse.leaves_first ? { leaves_first: true } : {}),
          ...(out.inverse.prune ? { prune: out.inverse.prune } : {}),
        }
      }
      return local
    } catch (e) {
      setErrors(policyErrors(e))
      setStatus('error')
      return null
    }
  }, [transport, objType, objName, load])

  const apply = useCallback(async (diff: CustomizeDiff, label = 'change') => {
    const inverse = await send(diff)
    if (inverse) {
      undoStack.current.push({ diff: inverse, label })
      redoStack.current = []
      logRef.current = [...logRef.current, label]
      bump((n) => n + 1)
    }
    return !!inverse
  }, [send])

  // Move one step between the stacks. The entry leaves its stack only once
  // the server stored it, so a refused undo / redo can be tried again.
  const step = useCallback(async (
    from: typeof undoStack, to: typeof undoStack, forward: boolean,
  ) => {
    const top = from.current[from.current.length - 1]
    if (!top) return
    const back = await send(top.diff)
    if (back) {
      from.current.pop()
      to.current.push({ diff: back, label: top.label })
      if (forward) logRef.current = [...logRef.current, top.label]
      else {
        const i = logRef.current.lastIndexOf(top.label)
        if (i >= 0) logRef.current = [...logRef.current.slice(0, i), ...logRef.current.slice(i + 1)]
      }
    }
    bump((n) => n + 1)
  }, [send])
  const undo = useCallback(() => step(undoStack, redoStack, false), [step])
  const redo = useCallback(() => step(redoStack, undoStack, true), [step])

  const resetUnder = useCallback(async (prefixes: string[], label = 'reset') => {
    const paths = overlayLeafPaths(overlayRef.current)
      .filter((p) => prefixes.some((pre) => p === pre || p.startsWith(`${pre}.`)))
    if (!paths.length) return true
    return apply({ set: [], reset: paths }, label)
  }, [apply])

  const resetAll = useCallback(async () => {
    setStatus('saving')
    try {
      await transport.reset(objType, objName)
      undoStack.current = []
      redoStack.current = []
      await load()
      setStatus('saved')
      return true
    } catch (e) {
      setErrors(policyErrors(e))
      setStatus('error')
      return false
    }
  }, [transport, objType, objName, load])

  const overlay = (res?.overlay || {}) as Record<string, unknown>
  const top = undoStack.current[undoStack.current.length - 1]
  const next = redoStack.current[redoStack.current.length - 1]
  return {
    objType,
    objName,
    status,
    errors,
    overlay,
    effective: (res?.effective as Record<string, unknown>) ?? null,
    core: (res?.core as Record<string, unknown>) ?? null,
    apply,
    undo,
    canUndo: undoStack.current.length > 0,
    undoLabel: top?.label || '',
    redo,
    canRedo: redoStack.current.length > 0,
    redoLabel: next?.label || '',
    resetUnder,
    resetAll,
    isCustomized: (path) => overlayValueAt(overlay, path).present,
    countCustomized: (prefix) => overlayLeafPaths(overlay)
      .filter((p) => !prefix || p === prefix || p.startsWith(`${prefix}.`)).length,
    dismissErrors: () => setErrors([]),
    changeLog: logRef.current,
    warnings,
  }
}
